import { useEffect, useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions, DeleteRecordButton } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, DateInput, TextArea, SelectInput, LineTable } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel, num, physicalAssetStatus, serviceCycleStatus, serviceCycleScenario } from '../../lib/format';
import type {
  PhysicalAsset, AssetServiceCycle, AssetIntakeResult, AssetTrace, AssetTraceHistoryRow,
  Customer, Item,
} from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);

// 来料登记命中候选行的具体类型：直接取自 AssetIntakeResult.candidates，避免 any 断言。
type AssetCandidate = NonNullable<AssetIntakeResult['candidates']>[number];

const CYCLE_SCENARIOS: { value: string; label: string }[] = [
  { value: 'repair', label: '维修' },
  { value: 'processing', label: '委外加工' },
  { value: 'inspection_only', label: '仅检测' },
  { value: 'other', label: '其他' },
];

// 维修版本可推进的状态（与 core-api CYCLE_STATUSES 对齐；交付/归还/关闭为终态方向）。
const NEXT_CYCLE_STATUS: Record<string, { to: string; label: string }[]> = {
  intake: [{ to: 'in_production', label: '开始生产' }],
  in_production: [{ to: 'inspected', label: '标记已检验' }, { to: 'ready', label: '标记待交付' }],
  inspected: [{ to: 'ready', label: '标记待交付' }, { to: 'in_production', label: '返修/继续生产' }],
  ready: [{ to: 'delivered', label: '交付客户' }, { to: 'returned', label: '归还客户' }],
  delivered: [{ to: 'closed', label: '关闭版本' }],
  returned: [{ to: 'closed', label: '关闭版本' }],
};

// —— 来料登记 / 同号判定面板：体现「同客户同序列号绝不报重复」的核心业务规则 ——
function IntakePanel({ onDone }: { onDone: () => void }) {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [itemLabel, setItemLabel] = useState('');
  const [f, setF] = useState<Record<string, string>>({ customer_serial: '', asset_type: '', first_received_date: today(), scenario: 'repair' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pending, setPending] = useState<AssetIntakeResult | null>(null); // 同号命中，等待业务判定
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const base = () => ({
    customer_id: customerId, customer_serial: f.customer_serial.trim(),
    item_id: itemId || null, asset_type: f.asset_type || null,
    description: f.description || null, first_received_date: f.first_received_date || null,
    scenario: f.scenario,
  });

  const detect = async () => {
    setErr(null); setOk(null); setPending(null);
    if (!customerId) { setErr('客户为必填'); return; }
    if (!f.customer_serial.trim()) { setErr('客户序列号为必填'); return; }
    setBusy(true);
    try {
      // 先探测：auto（不指定 resolution）。命中候选则由业务选择新增版本 / 另一件同号。
      const r = await api.post<{ data: AssetIntakeResult }>('/api/physical-assets/intake', base()).then((res) => res.data);
      if (r.resolution === 'new_asset') {
        setOk(`已登记首件（V1）：${r.asset?.customer_serial ?? ''}`);
        setF({ customer_serial: '', asset_type: '', first_received_date: today(), scenario: 'repair' });
        setTimeout(onDone, 700);
      } else if (r.needs_resolution) {
        setPending(r);
      } else {
        setOk('已登记');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '登记失败');
    } finally { setBusy(false); }
  };

  const chooseNewCycle = async (assetId: string) => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post<{ data: AssetIntakeResult }>('/api/physical-assets/intake', { ...base(), resolution: 'new_cycle', asset_id: assetId }).then((res) => res.data);
      setOk(`已对既有工件开启 V${r.cycle?.version ?? ''} 维修版本`);
      setPending(null);
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '新增维修版本失败');
    } finally { setBusy(false); }
  };

  const chooseNewAsset = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post<{ data: AssetIntakeResult }>('/api/physical-assets/intake', { ...base(), resolution: 'new_asset' }).then((res) => res.data);
      setOk(`已登记为另一件同号工件（${r.asset?.customer_serial ?? ''}）`);
      setPending(null);
      setTimeout(onDone, 700);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '新建同号工件失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>登记工件（客户返厂 / 首次入厂）</h2><div className="desc">写入核心服务 /api/physical-assets/intake · 同一客户下相同序列号【不会报重复】：系统命中候选后，由业务判定「对既有工件开启新维修版本」还是「登记为另一件同号工件」</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        {ok ? <div className="notice ok" style={{ marginBottom: 12 }}>{ok}</div> : null}
        <FormGrid>
          <Field label="客户" required>
            <SearchSelect<Customer> endpoint="/api/customers" value={customerId} label={customerLabel}
              onChange={(row) => { setCustomerId(row?.id ?? null); setCustomerLabel(row ? `${row.code} ${row.legal_name}` : ''); }}
              optionTitle={(c) => `${c.code} · ${c.legal_name}`} optionMeta={(c) => c.tax_id} placeholder="检索客户…" paginated />
          </Field>
          <Field label="客户序列号（原始，允许重复）" required>
            <TextInput value={f.customer_serial ?? ''} onChange={(e) => set('customer_serial', e.target.value)} placeholder="客户打的钢印 / 铭牌序列号" />
          </Field>
          <Field label="物料 / 工件类型（选填，客户件可能非公司物料）">
            <SearchSelect<Item> endpoint="/api/items" value={itemId} label={itemLabel}
              onChange={(row) => { setItemId(row?.id ?? null); setItemLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
          </Field>
          <Field label="工件类型（自由分类）"><TextInput value={f.asset_type ?? ''} onChange={(e) => set('asset_type', e.target.value)} placeholder="如：轧辊 / 轴 / 模具" /></Field>
          <Field label="业务场景">
            <SelectInput value={f.scenario} onChange={(e) => set('scenario', e.target.value)}>
              {CYCLE_SCENARIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </SelectInput>
          </Field>
          <Field label="首次入厂日期"><DateInput value={f.first_received_date ?? ''} onChange={(e) => set('first_received_date', e.target.value)} /></Field>
          <Field label="描述 / 来料初检发现" span><TextArea value={f.description ?? ''} onChange={(e) => set('description', e.target.value)} placeholder="尺寸 / 磨损 / 损伤 / 客户要求…" /></Field>
        </FormGrid>
        <div className="detail-actions" style={{ marginTop: 10 }}>
          <button className="btn primary" onClick={detect} disabled={busy}>{busy ? '处理中…' : '检测并登记'}</button>
        </div>

        {pending ? (
          <div className="notice warn" style={{ display: 'block', marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{pending.prompt || '同一客户下该序列号已存在工件，请判定处理方式。'}</div>
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>命中 {pending.match_count ?? pending.candidates?.length ?? 0} 件候选：</div>
            <LineTable<AssetCandidate>
              rows={pending.candidates ?? []}
              emptyText="无候选"
              columns={[
                { header: '序列号', render: (c) => <span className="num">{c.customer_serial}</span> },
                { header: '类型 / 物料', render: (c) => `${c.asset_type ?? ''} ${c.item_code ?? ''}`.trim() || '—' },
                { header: '当前版本', align: 'right', render: (c) => <span className="num">V{c.current_version}</span> },
                { header: '状态', render: (c) => <Pill tone={physicalAssetStatus(c.status).tone} label={physicalAssetStatus(c.status).label} /> },
                { header: '判定', render: (c) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn" disabled={busy} onClick={() => chooseNewCycle(c.id)}>新增维修版本</button>
                  </div>
                ) },
              ]}
            />
            <div className="detail-actions" style={{ marginTop: 10 }}>
              <button className="btn" disabled={busy} onClick={chooseNewAsset}>登记为另一件同号工件</button>
              <button className="btn" disabled={busy} onClick={() => setPending(null)}>取消</button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// —— 单个维修版本：状态推进 ——
function CycleRow({ cycle, onChanged }: { cycle: AssetServiceCycle; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'physical-assets', 'create');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const transition = async (to: string) => {
    setErr(null); setBusy(true);
    try {
      const body: Record<string, unknown> = { status: to };
      if (['delivered', 'returned', 'closed'].includes(to)) body.returned_date = today();
      await api.post(`/api/physical-assets/cycles/${cycle.id}/status`, body);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '状态更新失败');
    } finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="num strong">V{cycle.version}</span>
        <Pill tone={serviceCycleStatus(cycle.status).tone} label={serviceCycleStatus(cycle.status).label} />
        <Pill tone="info" label={serviceCycleScenario(cycle.scenario)} />
        <span className="muted" style={{ fontSize: 12 }}>接收入厂 {dateLabel(cycle.intake_date)}{cycle.returned_date ? ` · 归还 ${dateLabel(cycle.returned_date)}` : ''}</span>
        {cycle.work_order_number ? <span className="muted" style={{ fontSize: 12 }}>工单 {cycle.work_order_number}</span> : null}
        {cycle.contract_number ? <span className="muted" style={{ fontSize: 12 }}>合同 {cycle.contract_number}</span> : null}
      </div>
      {(cycle.findings || cycle.recommendation) ? (
        <div style={{ fontSize: 12.5, marginTop: 4, color: 'var(--ink-2)' }}>
          {cycle.findings ? <div>初检：{cycle.findings}</div> : null}
          {cycle.recommendation ? <div>建议：{cycle.recommendation}</div> : null}
        </div>
      ) : null}
      {err ? <div className="notice bad" style={{ marginTop: 6, marginBottom: 0 }}>{err}</div> : null}
      {canWrite && (NEXT_CYCLE_STATUS[cycle.status] ?? []).length ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          {(NEXT_CYCLE_STATUS[cycle.status] ?? []).map((s) => (
            <button key={s.to} className="btn" disabled={busy} onClick={() => transition(s.to)}>{s.label}</button>
          ))}
        </div>
      ) : null}
      <div className="row-actions" style={{ marginTop: 6 }}>
        {/* 仅管理员渲染。删的是「这一条维修版本」，不连带工件主档和其他版本；工单侧的绑定会一并断开。 */}
        <DeleteRecordButton
          type="asset-service-cycles"
          id={cycle.id}
          label={`维修版本 V${cycle.version}`}
          note={cycle.work_order_number ? `同时断开与工单 ${cycle.work_order_number} 的双向绑定，该工单随后可作废或删除。` : undefined}
          onDone={onChanged}
        />
      </div>
    </div>
  );
}

// —— 新增维修版本表单 ——
function NewCycleForm({ assetId, onCreated, onCancel }: { assetId: string; onCreated: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ scenario: 'repair', intake_date: today() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));
  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      await api.post(`/api/physical-assets/${assetId}/service-cycles`, {
        scenario: f.scenario, status: 'intake', intake_date: f.intake_date || null,
        findings: f.findings || null, recommendation: f.recommendation || null, notes: f.notes || null,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '新增维修版本失败');
    } finally { setBusy(false); }
  };
  return (
    <div className="notice info" style={{ display: 'block', marginTop: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>开启新的维修版本（V+1，不复制工件主档）</div>
      {err ? <div className="notice bad" style={{ marginBottom: 8 }}>{err}</div> : null}
      <FormGrid min={160}>
        <Field label="场景">
          <SelectInput value={f.scenario} onChange={(e) => set('scenario', e.target.value)}>
            {CYCLE_SCENARIOS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </SelectInput>
        </Field>
        <Field label="接收入厂日期"><DateInput value={f.intake_date ?? ''} onChange={(e) => set('intake_date', e.target.value)} /></Field>
        <Field label="来料初检发现" span><TextArea value={f.findings ?? ''} onChange={(e) => set('findings', e.target.value)} /></Field>
        <Field label="处理建议" span><TextArea value={f.recommendation ?? ''} onChange={(e) => set('recommendation', e.target.value)} /></Field>
      </FormGrid>
      <div className="detail-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '确认新增版本'}</button>
        <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
      </div>
    </div>
  );
}

function AssetDetail({ row, onChanged }: { row: PhysicalAsset; onChanged: () => void }) {
  const { user } = useAuth();
  const canWrite = can(user, 'physical-assets', 'create');
  const { data, loading, error, reload } = useDetail<PhysicalAsset>(`/api/physical-assets/${row.id}`);
  const [trace, setTrace] = useState<AssetTrace | null>(null);
  const [traceErr, setTraceErr] = useState<string | null>(null);
  const [newCycleOpen, setNewCycleOpen] = useState(false);
  const a = data ?? row;

  useEffect(() => {
    let alive = true;
    setTrace(null); setTraceErr(null);
    api.detail<AssetTrace>(`/api/physical-assets/${row.id}/trace`)
      .then((t) => { if (alive) setTrace(t); })
      .catch((e) => { if (alive) setTraceErr(e instanceof ApiError ? e.message : '履历加载失败'); });
    return () => { alive = false; };
  }, [row.id, a.updated_at]);

  const bump = () => { reload(); onChanged(); };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id num">{a.customer_serial}</span>
        <h3>{a.asset_type || a.item_name_zh || '工件主档'}</h3>
        <Pill tone={physicalAssetStatus(a.status).tone} label={physicalAssetStatus(a.status).label} />
        <Pill tone="neutral" label={`当前 V${a.current_version}`} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      <div className="kv-grid">
        <KeyValue k="客户">{`${a.customer_code ?? ''} ${a.customer_name ?? ''}`.trim() || a.customer_id}</KeyValue>
        <KeyValue k="物料 / 工件类型">{`${a.item_code ?? ''} ${a.item_name_zh ?? ''}`.trim() || a.asset_type || '—（客户件非公司物料）'}</KeyValue>
        <KeyValue k="内部编码">{a.internal_code || '—'}</KeyValue>
        <KeyValue k="首次入厂">{dateLabel(a.first_received_date)}</KeyValue>
        <KeyValue k="来源系统 / 编号">{a.source_system ? `${a.source_system} / ${a.source_id ?? ''}` : '—'}</KeyValue>
        <KeyValue k="ERPNext / QC 映射">{`${a.legacy_erpnext_id ?? ''} ${a.legacy_qc_id ?? ''}`.trim() || '—'}</KeyValue>
      </div>
      <div className="notice info" style={{ marginTop: 10, display: 'block' }}>
        <div style={{ fontSize: 12.5 }}>同一工件多次返厂只新增「维修版本 V1/V2/V3…」，不复制工件主档；客户序列号允许跨工件重复，由客户 + 工件类型识别。</div>
      </div>

      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '12px 0 6px' }}>维修履历（版本）</div>
      {(a.service_cycles ?? []).map((c) => (
        <CycleRow key={c.id} cycle={c} onChanged={bump} />
      ))}
      {canWrite ? (
        <div className="detail-actions" style={{ marginTop: 6 }}>
          <button className="btn primary" onClick={() => setNewCycleOpen((v) => !v)}>{newCycleOpen ? '收起' : '开启新的维修版本'}</button>
        </div>
      ) : null}
      {newCycleOpen && canWrite ? (
        <NewCycleForm assetId={a.id} onCancel={() => setNewCycleOpen(false)} onCreated={() => { setNewCycleOpen(false); bump(); }} />
      ) : null}

      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '14px 0 6px' }}>全链路追溯（工单 / 领料·完工 / 检验 / 交货）</div>
      {traceErr ? <div className="notice bad">{traceErr}</div> : null}
      {!trace && !traceErr ? <Spinner label="加载履历…" /> : null}
      {trace ? (
        <LineTable<AssetTraceHistoryRow>
          rows={trace.history}
          emptyText="暂无关联履历数据"
          columns={[
            { header: '版本', render: (h) => <span className="num">V{h.version}</span> },
            { header: '状态', render: (h) => <Pill tone={serviceCycleStatus(h.status).tone} label={serviceCycleStatus(h.status).label} /> },
            { header: '工单', render: (h) => h.work_order_number || '—' },
            { header: '生产流水', render: (h) => h.stock_movements.length ? h.stock_movements.join('、') : '—' },
            { header: '质检单', render: (h) => h.inspections.length ? h.inspections.join('、') : '—' },
            { header: '交货单', render: (h) => h.deliveries.length ? h.deliveries.join('、') : '—' },
          ]}
        />
      ) : null}
    </div>
  );
}

export function PhysicalAssetsPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const canWrite = can(user, 'physical-assets', 'create');
  const bump = () => setReloadSignal((n) => n + 1);

  const columns: Column<PhysicalAsset>[] = [
    { header: '客户序列号', render: (a) => <span className="num strong">{a.customer_serial}</span> },
    { header: '客户', render: (a) => a.customer_name || '—' },
    { header: '类型 / 物料', render: (a) => `${a.asset_type ?? ''} ${a.item_name_zh ?? ''}`.trim() || '—' },
    { header: '当前版本', align: 'right', render: (a) => <span className="num">V{a.current_version}</span> },
    { header: '版本数', align: 'right', render: (a) => <span className="num muted">{num(a.cycle_count ?? 0, 0)}</span> },
    { header: '首次入厂', render: (a) => <span className="muted">{dateLabel(a.first_received_date)}</span> },
    { header: '状态', render: (a) => <Pill tone={physicalAssetStatus(a.status).tone} label={physicalAssetStatus(a.status).label} /> },
  ];

  return (
    <>
      {intakeOpen && canWrite ? (
        <IntakePanel onDone={() => { setIntakeOpen(false); bump(); }} />
      ) : null}
      <ListPage<PhysicalAsset>
        title="工件主档 · 维修履历"
        desc="阶段6 · /api/physical-assets。以「工件身份（不可变内部ID）+ 维修版本」模型管理激光熔覆返厂履历：同客户同序列号不报重复，每次返厂新增版本；工单、领料/完工、质检、交货都挂到具体版本，供 portal 全链路追溯。"
        endpoint="/api/physical-assets"
        columns={columns}
        rowId={(a) => a.id}
        showYear={false}
        searchPlaceholder="搜索序列号 / 内部编码 / 客户 / 工件类型…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="physical-assets" row={row} fields={RECORD_FIELDS['physical-assets']} label={String(row.customer_serial)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setIntakeOpen((v) => !v)}>{intakeOpen ? '收起登记' : '登记工件'}</button>
        ) : undefined}
        renderDetail={(row) => <AssetDetail row={row} onChanged={bump} />}
      />
    </>
  );
}
